import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../apiClient';
import { Profile } from '../../hooks/useAuth';
import { Save, CheckCircle2, AlertCircle, Globe, Building2, Lock, Clock } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';

interface ProviderProfile {
  business_name: string;
  description: string;
  category: string;
  location: string;
  slug: string;
  contact_number: string;
  bank_name: string;
  bank_account_number: string;
  bank_account_name: string;
}

interface ScheduleDay {
  day_of_week: number;
  is_open: boolean;
  open_time: string;
  close_time: string;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const DEFAULT_SCHEDULE: ScheduleDay[] = Array.from({ length: 7 }, (_, i) => ({
  day_of_week: i,
  is_open: i >= 1 && i <= 5,
  open_time: '09:00',
  close_time: '17:00',
}));

export const StorefrontSettings: React.FC<{ profile: Profile }> = () => {
  const [form, setForm] = useState<ProviderProfile>({
    business_name: '', description: '', category: '', location: '',
    slug: '', contact_number: '', bank_name: '', bank_account_number: '', bank_account_name: '',
  });
  const [schedule, setSchedule] = useState<ScheduleDay[]>(DEFAULT_SCHEDULE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      apiFetch('/provider/profile'),
      apiFetch('/provider/schedule'),
    ]).then(([profileData, scheduleData]) => {
      if (profileData.success) setForm(f => ({ ...f, ...profileData.profile }));
      else setError(profileData.message);
      if (scheduleData.success) setSchedule(scheduleData.schedule);
    }).catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const set = (field: keyof ProviderProfile) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const updateDay = (dayIndex: number, field: keyof ScheduleDay, value: any) => {
    setSchedule(prev => prev.map(d =>
      d.day_of_week === dayIndex ? { ...d, [field]: value } : d
    ));
  };

  const flash = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const { slug, ...payload } = form;
      const data = await apiFetch('/provider/profile', { method: 'PUT', body: JSON.stringify(payload) });
      if (!data.success) throw new Error(data.message);
      flash('Profile saved successfully.');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    setError(null);
    try {
      const data = await apiFetch('/provider/schedule', {
        method: 'PUT',
        body: JSON.stringify({ schedule }),
      });
      if (!data.success) throw new Error(data.message);
      flash('Working hours saved.');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingSchedule(false);
    }
  };

  if (loading) return (
    <div className="p-12 flex items-center justify-center">
      <p className="label-caps text-ink-faint">Loading profile...</p>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-10 space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-3">
        <div>
          <h2 className="display text-2xl md:text-3xl text-ink">Storefront settings</h2>
          <p className="label-caps text-ink-faint mt-2">Public profile, working hours &amp; bank details</p>
        </div>
        <a
          href={`/#/p/${form.slug}`} target="_blank" rel="noreferrer"
          className="flex items-center gap-2 text-accent label-caps hover:underline min-h-[44px] items-center"
        >
          <Globe size={14} /> View storefront
        </a>
      </div>

      {error && (
        <div className="p-4 flex items-center gap-3 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 rounded-sm border border-red-200 dark:border-red-900 text-xs font-semibold">
          <AlertCircle size={16} className="shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div className="p-4 flex items-center gap-3 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 rounded-sm border border-green-200 dark:border-green-900 text-xs font-semibold">
          <CheckCircle2 size={16} className="shrink-0" /> {success}
        </div>
      )}

      <form onSubmit={handleSaveProfile} className="space-y-6">
        <Card accent className="p-6 md:p-8 space-y-5">
          <div className="flex items-center gap-3">
            <Globe size={18} className="text-accent" />
            <h3 className="display text-lg text-ink">Public profile</h3>
          </div>
          <Input label="Business name" value={form.business_name} onChange={set('business_name')} placeholder="Your business name" />
          <Textarea label="Description" value={form.description} onChange={set('description')} placeholder="Tell customers what you offer..." rows={3} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Input label="Category" value={form.category} onChange={set('category')} placeholder="e.g. Salon, Clinic, Tutor" />
            <Input label="Location" value={form.location} onChange={set('location')} placeholder="e.g. Kuala Lumpur" />
          </div>
          <Input label="Contact number" value={form.contact_number} onChange={set('contact_number')} placeholder="+60 12-345 6789" />
          <Input label="Storefront URL (read-only)" value={`/p/${form.slug}`} onChange={() => {}} readOnly className="opacity-60 cursor-not-allowed" />
        </Card>

        <Card className="p-6 md:p-8 space-y-5">
          <div className="flex items-center gap-3">
            <Building2 size={18} className="text-ink-muted" />
            <h3 className="display text-lg text-ink">Bank details</h3>
            <Lock size={14} className="text-ink-faint" />
          </div>
          <p className="label-caps text-ink-faint">
            Pre-filled when requesting withdrawals. Not shown to customers.
          </p>
          <Input label="Bank name" value={form.bank_name} onChange={set('bank_name')} placeholder="e.g. Maybank, CIMB, RHB" />
          <Input label="Account number" value={form.bank_account_number} onChange={set('bank_account_number')} placeholder="Account number" />
          <Input label="Account holder name" value={form.bank_account_name} onChange={set('bank_account_name')} placeholder="Name as per bank account" />
        </Card>

        <div className="flex justify-end">
          <Button type="submit" variant="primary" disabled={saving}>
            <Save size={16} /> {saving ? 'Saving...' : 'Save profile'}
          </Button>
        </div>
      </form>

      {/* Working hours */}
      <Card accent className="p-6 md:p-8 space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Clock size={18} className="text-accent" />
            <h3 className="display text-lg text-ink">Working hours</h3>
          </div>
          <Button type="button" variant="primary" onClick={handleSaveSchedule} disabled={savingSchedule}>
            <Save size={14} /> {savingSchedule ? 'Saving...' : 'Save hours'}
          </Button>
        </div>
        <p className="label-caps text-ink-faint">
          Customers can only book during your open hours. Closed days are hidden on your storefront.
        </p>

        <div className="space-y-3">
          {schedule.map(day => (
            <div key={day.day_of_week}
              className={`flex flex-wrap items-center gap-3 md:gap-4 p-4 rounded-sm border transition-colors ${
                day.is_open ? 'border-line bg-raised' : 'border-line bg-surface opacity-60'
              }`}
            >
              <button
                type="button"
                onClick={() => updateDay(day.day_of_week, 'is_open', !day.is_open)}
                aria-label={`${day.is_open ? 'Close' : 'Open'} ${DAY_NAMES[day.day_of_week]}`}
                className={`w-11 h-6 rounded-full relative transition-colors shrink-0 ${
                  day.is_open ? 'bg-accent' : 'bg-line-strong'
                }`}
              >
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-raised shadow transition-transform ${
                  day.is_open ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </button>

              <span className="label-caps text-ink w-24 shrink-0">
                {DAY_NAMES[day.day_of_week]}
              </span>

              {day.is_open ? (
                <div className="flex items-center gap-2 md:gap-3 flex-1 flex-wrap">
                  <input
                    type="time" value={day.open_time}
                    onChange={e => updateDay(day.day_of_week, 'open_time', e.target.value)}
                    className="bg-surface border border-line-strong rounded-sm px-3 min-h-[44px] text-sm font-semibold text-ink outline-none focus:border-accent transition-colors"
                  />
                  <span className="label-caps text-ink-faint">to</span>
                  <input
                    type="time" value={day.close_time}
                    onChange={e => updateDay(day.day_of_week, 'close_time', e.target.value)}
                    className="bg-surface border border-line-strong rounded-sm px-3 min-h-[44px] text-sm font-semibold text-ink outline-none focus:border-accent transition-colors"
                  />
                  <span className="label-caps text-ink-faint">
                    {(() => {
                      const [oh, om] = day.open_time.split(':').map(Number);
                      const [ch, cm] = day.close_time.split(':').map(Number);
                      const diff = (ch * 60 + cm) - (oh * 60 + om);
                      return diff > 0 ? `${Math.floor(diff / 60)}h ${diff % 60 > 0 ? `${diff % 60}m` : ''}` : '';
                    })()}
                  </span>
                </div>
              ) : (
                <span className="label-caps text-ink-faint">Closed</span>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
